import { FabricAuthState } from '@common/Fabric/constants';
import { useUnifiedFabricContext } from '@common/Fabric/UnifiedFabrics/context';
import { Private_Routes } from '@common/url';
import { useFabricFormCtxOAuthLogin } from '@pages/Fabric/hooks/useFabricFormCtxOAuthLogin';
import { FABRIC_TABS, FormContexts } from '@pages/Fabric/types';
import { useWorkspaceFabricContext } from '@pages/UserSettings/contexts/WorkspaceFabricContext/context';
import { LogoDbxSfBqIcon } from '@prophecy/icons';
import { BaseColor, getColorCode, theme } from '@prophecy/theme';
import { Badges, Button, Shimmer, Stack, Text } from '@prophecy/ui-v3';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { FabricSetupState } from '../../contexts/constants';
import { PROVIDER_META } from './providerMeta';

// ─── Types ───────────────────────────────────────────────────────────────────

type BannerAction = 'setupEnvironment' | 'reconfigure' | 'viewConfig' | 'signIn';

type ButtonConfig = {
  label: string;
  variant: 'primary' | 'secondary';
  action: BannerAction;
};

type BannerConfig = {
  title: string;
  subtitle: string;
  icon: ReactNode;
  iconError?: boolean;
  tag?: { label: string; variant: 'success' | 'error' };
  buttons: ButtonConfig[];
  error?: boolean;
};

// ─── Config factory ───────────────────────────────────────────────────────────

const getBannerConfig = ({
  authState,
  providerName,
  providerIcon,
  isAdmin
}: {
  authState: Exclude<FabricAuthState, FabricAuthState.Loading> | FabricSetupState;
  providerName: string;
  providerIcon: ReactNode;
  isAdmin: boolean;
}): BannerConfig => {
  const sharedSubtitle = isAdmin
    ? `New projects will run on ${providerName}. Projects already on Prophecy's managed warehouse aren't affected.`
    : `Your workspace admin made ${providerName} the default environment. New projects will run here.`;

  const CONFIGS: Record<Exclude<FabricAuthState, FabricAuthState.Loading> | FabricSetupState, BannerConfig> = {
    [FabricSetupState.UnConfigured]: {
      title: 'Run projects on your own data platform',
      subtitle: isAdmin
        ? "You're on Prophecy's managed warehouse. Connect Databricks, Snowflake, or BigQuery whenever you're ready."
        : "You're on Prophecy's managed warehouse. Ask your admin to connect Databricks, Snowflake, or BigQuery whenever you're ready.",
      icon: <StyledLogoDbxSfBqIcon />,
      buttons: isAdmin
        ? [{ label: 'Set Up Environment', variant: 'primary' as const, action: 'setupEnvironment' as const }]
        : []
    },

    [FabricSetupState.Configured]: {
      title: `${providerName} is now your default environment`,
      subtitle: sharedSubtitle,
      icon: providerIcon,
      tag: { label: 'Configured', variant: 'success' },
      buttons: [
        {
          label: isAdmin ? 'Re-Configure' : 'View Config',
          variant: 'secondary',
          action: isAdmin ? 'reconfigure' : 'viewConfig'
        }
      ]
    },

    [FabricAuthState.AdminWithValidCreds]: {
      title: `${providerName} is now your default environment`,
      subtitle: sharedSubtitle,
      icon: providerIcon,
      tag: { label: 'Configured', variant: 'success' },
      buttons: [
        {
          label: isAdmin ? 'Re-Configure' : 'View Config',
          variant: 'secondary',
          action: isAdmin ? 'reconfigure' : 'viewConfig'
        }
      ]
    },

    [FabricAuthState.NonAdminWithValidCreds]: {
      title: `${providerName} is now your default environment`,
      subtitle: sharedSubtitle,
      icon: providerIcon,
      tag: { label: 'Configured', variant: 'success' },
      buttons: [
        {
          label: isAdmin ? 'Re-Configure' : 'View Config',
          variant: 'secondary',
          action: isAdmin ? 'reconfigure' : 'viewConfig'
        }
      ]
    },

    [FabricAuthState.OAuthValid]: {
      title: `${providerName} is now your default environment`,
      subtitle: sharedSubtitle,
      icon: providerIcon,
      tag: { label: 'Configured', variant: 'success' },
      buttons: [
        {
          label: isAdmin ? 'Re-Configure' : 'View Config',
          variant: 'secondary',
          action: isAdmin ? 'reconfigure' : 'viewConfig'
        }
      ]
    },

    [FabricAuthState.OAuthExpired]: {
      title: `Sign in to ${providerName} to continue`,
      subtitle: sharedSubtitle,
      icon: providerIcon,
      iconError: true,
      tag: { label: 'Re-auth Required', variant: 'error' },
      buttons: [
        { label: 'View Config', variant: 'secondary', action: 'viewConfig' },
        { label: `Sign in to ${providerName}`, variant: 'primary', action: 'signIn' }
      ],
      error: true
    },

    [FabricAuthState.OAuthNotConnected]: {
      title: `Sign in to ${providerName} to continue`,
      subtitle: sharedSubtitle,
      icon: providerIcon,
      iconError: true,
      tag: { label: 'Auth Pending', variant: 'error' },
      buttons: [
        { label: 'View Config', variant: 'secondary', action: 'viewConfig' },
        { label: `Sign in to ${providerName}`, variant: 'primary', action: 'signIn' }
      ],
      error: true
    },

    [FabricAuthState.AdminWithExpiredCreds]: {
      title: 'Update your Personal Access Token',
      subtitle: sharedSubtitle,
      icon: providerIcon,
      iconError: true,
      tag: { label: 'Re-auth Required', variant: 'error' },
      buttons: [{ label: 'Re-Configure', variant: 'secondary', action: 'reconfigure' }],
      error: true
    },

    [FabricAuthState.NonAdminWithExpiredCreds]: {
      title: 'Environment not accessible.',
      subtitle: 'Personal Access Token expired. Ask your Admin to re-authenticate the environment to continue.',
      icon: providerIcon,
      iconError: true,
      tag: { label: 'Re-auth Required', variant: 'error' },
      buttons: [{ label: 'View Config', variant: 'secondary', action: 'viewConfig' }],
      error: true
    }
  };

  return CONFIGS[authState];
};

// ─── Styled components ────────────────────────────────────────────────────────

const BannerCard = styled(Stack)<{ $error?: boolean }>`
  border: 1px solid ${({ $error }) => ($error ? theme.colors.error300 : theme.colors.gray200)};
  border-radius: ${theme.radius.x8};
  padding: ${theme.spaces.x20};
  background: ${({ $error }) => ($error ? theme.colors.error25 : theme.colors.gray50)};
`;

const StyledButton = styled(Button)`
  flex-shrink: 0;
`;

const ProviderIconBadge = styled.div<{ $color: `${BaseColor}` }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${theme.sizes.x48};
  height: ${theme.sizes.x48};
  border-radius: 50%;
  background: ${({ $color }) => getColorCode($color, 50)};

  svg {
    width: ${theme.sizes.x24};
    height: ${theme.sizes.x24};
  }
`;

const StyledContentOuter = styled(Stack)`
  flex: 1;
  min-width: 0;
`;

const StyledLogoDbxSfBqIcon = styled(LogoDbxSfBqIcon)`
  width: ${theme.sizes.x48};
  height: ${theme.sizes.x48};
  flex-shrink: 0;
`;

// ─── Skeleton placeholders ──────────────────────────────────────────────────────

const SkeletonCircle = styled.div`
  width: ${theme.sizes.x48};
  height: ${theme.sizes.x48};
  border-radius: 50%;
  flex-shrink: 0;
  background: ${theme.colors.gray200};
`;

// Each bar sits in a row whose height equals the real Text line-height, so the skeleton's
// text block is exactly as tall as the rendered banner's — avoiding any layout shift.
const SkeletonTextRow = styled.div<{ $lineHeight: string }>`
  height: ${({ $lineHeight }) => $lineHeight};
  display: flex;
  align-items: center;
`;

const SkeletonBar = styled.div<{ $width: string }>`
  width: ${({ $width }) => $width};
  height: ${theme.sizes.x12};
  border-radius: ${theme.radius.x4};
  background: ${theme.colors.gray200};
`;

const SkeletonButton = styled.div`
  width: 120px;
  height: ${theme.sizes.x32};
  border-radius: ${theme.radius.x8};
  flex-shrink: 0;
  background: ${theme.colors.gray200};
`;

const SetupEnvironmentBannerSkeleton = () => (
  <Shimmer loading fullWidth>
    <BannerCard direction='horizontal' align='space-between' alignY='center' gap={theme.spaces.x16}>
      <StyledContentOuter direction='horizontal' gap={theme.spaces.x12} alignY='center'>
        <SkeletonCircle />
        {/* mirrors the real banner: a Text level='md' title (24px) and a Text level='sm' subtitle (20px) with an x4 gap */}
        <Stack gap={theme.spaces.x4}>
          <SkeletonTextRow $lineHeight={theme.sizes.x24}>
            <SkeletonBar $width='240px' />
          </SkeletonTextRow>
          <SkeletonTextRow $lineHeight={theme.sizes.x20}>
            <SkeletonBar $width='360px' />
          </SkeletonTextRow>
        </Stack>
      </StyledContentOuter>
      <SkeletonButton />
    </BannerCard>
  </Shimmer>
);

export const SetupEnvironmentBanner = ({ formContexts }: { formContexts: FormContexts }) => {
  const navigate = useNavigate();
  const { isAdmin, fabricSetupState, fabricProvider } = useWorkspaceFabricContext();
  const fabricAuthState = useUnifiedFabricContext((state) => state.fabricAuthState);
  const { initiateLogin } = useFabricFormCtxOAuthLogin();

  const providerMeta = fabricProvider ? PROVIDER_META[fabricProvider] : undefined;
  const providerName = providerMeta?.displayName ?? 'your environment';
  const providerIcon = (
    <ProviderIconBadge $color={providerMeta?.color ?? BaseColor.grayBlue}>
      {providerMeta ? <providerMeta.icon /> : <LogoDbxSfBqIcon />}
    </ProviderIconBadge>
  );

  if (fabricAuthState === FabricAuthState.Loading) {
    return <SetupEnvironmentBannerSkeleton />;
  }

  const destination = Private_Routes.Settings.tab.getUrl({ tab: 'connections' }, { action: 'environment' });

  const ACTION_HANDLERS: Record<BannerAction, () => void> = {
    setupEnvironment: () => navigate(destination),
    reconfigure: () => navigate(destination),
    viewConfig: () => navigate(destination),
    signIn: async () => {
      await initiateLogin(formContexts[FABRIC_TABS.providers].getValues());
    }
  };

  const config = getBannerConfig({
    authState: fabricAuthState || fabricSetupState,
    providerName,
    providerIcon,
    isAdmin
  });

  return (
    <BannerCard
      direction='horizontal'
      align='space-between'
      alignY='center'
      gap={theme.spaces.x16}
      $error={config.error}
      data-test-id='setup-env-banner'>
      <StyledContentOuter direction='horizontal' gap={theme.spaces.x12} alignY='center'>
        {config.icon}

        <Stack gap={theme.spaces.x4}>
          <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
            <Text level='md' weight={theme.fontWeight.semiBold} tone={theme.textColor.secondary}>
              {config.title}
            </Text>
            {config.tag && (
              <Badges tone={config.tag.variant === 'success' ? 'success' : 'error'} variant='pillLight'>
                {config.tag.label}
              </Badges>
            )}
          </Stack>
          <Text level='sm' tone={theme.textColor.quaternary}>
            {config.subtitle}
          </Text>
        </Stack>
      </StyledContentOuter>

      <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
        {config.buttons.map((btn) => (
          <StyledButton
            key={btn.label}
            variant={btn.variant}
            size='s'
            onClick={ACTION_HANDLERS[btn.action]}
            data-test-id={`setup-env-banner-${btn.action}`}>
            {btn.label}
          </StyledButton>
        ))}
      </Stack>
    </BannerCard>
  );
};
